import { memo, useContext, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { Typography } from 'antd';
import { AppContext } from '../../../App';
import { ReactComponent as Incoming } from '../../../Assets/yesbank/incoming.svg';
import { ReactComponent as Investment } from '../../../Assets/yesbank/investment.svg';
import { ReactComponent as OutGoing } from '../../../Assets/yesbank/outgoing.svg';
import {
  convertAmoutToINRWithNotation,
  formatToINR,
} from '../../../Utils/utils';
import EmptyStateGraph from './EmptyStateChart';
import styles from './index.module.css';

interface CashFlowProjectionProps {
  value: any;
  noAccountSelected?: boolean;
}

const CashFlowProjection = ({ value, noAccountSelected }: CashFlowProjectionProps) => {
  const { isAmountVisible }: any = useContext(AppContext);

  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    if (
      value &&
      value.debit !== null &&
      value.credit !== null &&
      value.investment !== null
    ) {
      setData([
        { name: 'Income', value: value.credit },
        { name: 'Expense', value: value.debit },
        { name: 'Investment', value: value.investment },
      ]);
    }
  }, [value]);

  const isEmpty = useMemo(() => {
    if (data && data.length) {
      return data.filter((t) => t.value > 0).length > 0;
    } else return false;
  }, [data]);

  // Custom bar with vertical striped background and solid gradient fill
  const CustomBarWithStripes = (props: any) => {
    const { x, y, width, height, index } = props;
    const barRadius = 11; // Border radius for the bars

    const gradientId = `barGradient-${index}`;
    const stripeBgPatternId = `stripeBgPattern-${index}`;
    const clipPathId = `clipPath-${index}`;

    // Vertical stripe dimensions
    const stripeWidth = 3;
    const stripeGap = 3;

    return (
      <g>
        <defs>
          {/* Gradient for the solid bar fill */}
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(204, 64, 64, 0.4)" />
            <stop offset="75%" stopColor="rgba(200, 63, 63, 1)" />
          </linearGradient>

          {/* Vertical stripe pattern for background */}
          <pattern
            id={stripeBgPatternId}
            patternUnits="userSpaceOnUse"
            width={stripeWidth + stripeGap}
            height={height}
          >
            {/* Light purple vertical stripe */}
            <rect
              x="0"
              y="0"
              width={stripeWidth}
              height={height}
              fill="#F6F4FE"
            />
            {/* Gap (transparent) */}
            <rect
              x={stripeWidth}
              y="0"
              width={stripeGap}
              height={height}
              fill="transparent"
            />
          </pattern>

          {/* Clip path for rounded bar shape */}
          <clipPath id={clipPathId}>
            <rect
              x={x}
              y={y}
              width={width}
              height={height}
              rx={barRadius}
              ry={barRadius}
            />
          </clipPath>
        </defs>

        {/* Striped background (same size as data bar with vertical lines) */}
        {height > 0 && (
          <g clipPath={`url(#${clipPathId})`}>
            <rect
              x={x}
              y={y}
              width={width}
              height={height}
              fill={`url(#${stripeBgPatternId})`}
            />
          </g>
        )}

        {/* Actual data bar with solid gradient fill on top */}
        {height > 0 && (
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={barRadius}
            ry={barRadius}
            fill={`url(#${gradientId})`}
          />
        )}
      </g>
    );
  };

  // Custom Y-axis tick rendered as a single <text> element so long
  // formatted values (e.g. ₹1.2L) never wrap onto a second line.
  const CustomYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor='end'
        fill='#000000'
        fontSize={14}
        fontFamily='var(--font-family)'
      >
        {convertAmoutToINRWithNotation(payload.value)}
      </text>
    );
  };

  interface CustomTooltipProps extends TooltipProps<number, string> {}

  // Custom Tooltip Component
  const CustomTooltip: React.FC<CustomTooltipProps> = ({
    active,
    payload,
    label,
  }) => {
    if (active && payload && payload.length) {
      return (
        <div className={styles.tooltipContainer}>
          <div className={styles.tooltipHeader}>
            {label === 'Income' ? (
              <Incoming />
            ) : label === 'Expense' ? (
              <OutGoing />
            ) : (
              <Investment />
            )}

            <Typography.Text className={styles.tooltipLabel}>
              {label}
            </Typography.Text>
          </div>

          <Typography.Text className={styles.tooltipValue}>
            {formatToINR(
              payload && payload[0] && payload[0].value
                ? payload[0].value.toString()
                : '',
              isAmountVisible
            )}
          </Typography.Text>
        </div>
      );
    }

    return null;
  };

  return data && data.length && isEmpty ? (
    <div className={`cashFlowProjection ${styles.cashFlowProjection}`}>
      <div className={styles.chartWrapper}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, bottom: 10, left: 10 }}
            className='ybBarChart'
          >
            {/* Dashed horizontal grid lines */}
            <CartesianGrid
              strokeDasharray="11 9"
              stroke="#AFA5A5"
              strokeOpacity={0.4}
              vertical={false}
            />

            <YAxis
              tick={<CustomYAxisTick />}
              axisLine={false}
              tickLine={false}
              width={45}
            />

            <XAxis
              dataKey='name'
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#000000', fontSize: 15, fontFamily: 'var(--font-family)' }}
            />

            <Tooltip content={<CustomTooltip />} />

            <Bar
              dataKey='value'
              barSize={52}
              shape={(props: any) => <CustomBarWithStripes {...props} />}
            >
              {data.map((_entry, index) => (
                <Cell key={`cell-${index}`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  ) : (
    <EmptyStateGraph noAccountSelected={noAccountSelected} />
  );
};

export default memo(CashFlowProjection);
